import { inject, type InjectionKey, onMounted, onServerPrefetch, type Ref, toRaw } from 'vue';

export type SsgState = Record<string, unknown>;

export const SsgStateKey: InjectionKey<SsgState> = Symbol('ssg-state');

type StateRefs = Record<string, Ref<unknown>>;

interface UseSsgStateOptions<T extends StateRefs> {
  key: string | null;
  refs: T;
  isLoaded: () => boolean;
  load: () => Promise<void>;
  refreshOnClient?: boolean;
}

interface SsgStateHandle {
  hydrated: boolean;
  ready: () => Promise<void>;
}

const isSnapshot = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

const runLoad = (load: () => Promise<void>): Promise<void> =>
  load().catch((error: unknown) => {
    console.error('Error loading page data:', error);
  });

export function useSsgState<T extends StateRefs>({ key, refs, isLoaded, load, refreshOnClient = false }: UseSsgStateOptions<T>): SsgStateHandle {
  const state = inject(SsgStateKey, null);
  const snapshot = key !== null ? state?.[key] : undefined;
  let hydrated = false;

  if (!import.meta.env.SSR && isSnapshot(snapshot)) {
    for (const [name, target] of Object.entries(refs)) {
      if (name in snapshot) {
        target.value = snapshot[name];
      }
    }
    hydrated = isLoaded();
  }

  onServerPrefetch(async () => {
    if (!isLoaded()) {
      await load();
    }
    if (state && key !== null) {
      state[key] = Object.fromEntries(Object.entries(refs).map(([name, target]) => [name, toRaw(target.value)]));
    }
  });

  let clientLoad: Promise<void> | null = null;

  onMounted(() => {
    if (!hydrated) {
      clientLoad = runLoad(load);
      return;
    }
    if (refreshOnClient) {
      void runLoad(load);
    }
  });

  return {
    hydrated,
    ready: () => clientLoad ?? Promise.resolve(),
  };
}
